/**
 * Which pool this organisation's faces come from.
 *
 * Each option shows three real faces from its own pool, because this is a
 * choice made by looking — a list of four words would tell nobody anything.
 */
import { faceArt, FACE_STYLES, type FaceStyle } from '../components/primitives'
import { radius } from '../components/primitives/shape'

const LABELS: Record<FaceStyle, string> = {
  patterns: 'Patterns',
  notionists: 'Notionists',
  'open-peeps': 'Open Peeps',
  lorelei: 'Lorelei',
  historical: 'Historical',
  animals: 'Animals',
  storybook: 'Storybook',
}

const NOTES: Record<FaceStyle, string> = {
  patterns: 'built in',
  notionists: 'line art',
  'open-peeps': 'hand drawn',
  lorelei: 'illustrated',
  historical: 'real people',
  animals: 'creatures',
  storybook: 'a cast in costume',
}

/** Three slots far enough apart to show what a style actually looks like. */
const PREVIEW_SLOTS = [3, 10, 18]

export function FaceStyleChooser({ value, onChange }: {
  /**
   * The organisation's raw `face_style`, not narrowed to `FaceStyle`: an
   * organisation on its own pool carries `set:<slug>`, which matches none of
   * the five tiles below and so — correctly — leaves none of them pressed,
   * rather than a caller narrowing it first and making the wrong one look
   * selected.
   */
  value: string
  onChange: (style: FaceStyle) => void
}) {
  return (
    <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginBottom: 22 }}>
      {FACE_STYLES.map((style) => {
        const on = style === value
        return (
          <button
            key={style}
            aria-pressed={on}
            onClick={() => onChange(style)}
            style={{
              display: 'flex', alignItems: 'center', gap: 9, padding: '8px 13px 8px 9px',
              cursor: 'pointer', borderRadius: radius.sm,
              background: on ? 'var(--accent-wash)' : 'var(--panel)',
              border: `1px solid ${on ? 'var(--accent-edge)' : 'var(--border)'}`,
              color: on ? 'var(--accent-bright)' : 'var(--text-dim)',
            }}
          >
            <span style={{ display: 'flex' }} aria-hidden>
              {PREVIEW_SLOTS.map((slot, position) => {
                const art = faceArt('preview', `face:${String(slot).padStart(2, '0')}`, style)
                const shared = {
                  width: 26,
                  height: 26,
                  flex: 'none' as const,
                  marginLeft: position ? -6 : 0,
                  // Dark rather than light, so overlapping previews read as
                  // separate faces against the chip rather than the ground.
                  boxShadow: '0 0 0 1.5px rgba(0,0,0,.9)',
                }
                return art.kind === 'image'
                  ? (
                    <img
                      key={slot}
                      src={art.src}
                      alt=""
                      style={{ ...shared, objectFit: 'cover', borderRadius: 'var(--radius-face)' }}
                    />
                  )
                  : <span key={slot} style={{ ...shared, ...art.appearance, boxShadow: shared.boxShadow }} />
              })}
            </span>
            <span style={{ textAlign: 'left' }}>
              <span style={{ display: 'block', font: '700 12px var(--font-interface)' }}>
                {LABELS[style]}
              </span>
              <span style={{ display: 'block', font: '400 10px var(--font-machine)', color: 'var(--text-faint)', marginTop: 4 }}>
                {NOTES[style]}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}
